import type { AcquireBookZenbujiko } from "@/types/acquirebook";
import { applyZenbujikoGenzaiTransform } from "@/utils/utilsAcquireBook";
import type { BulkPropertyRow } from "../types";
import { type AcquirePolicy, usesNew } from "./acquirePolicy";
import {
  isUsableByWindow,
  pickCandidatePool,
  zenbujikoFields,
} from "./reuseAvailability";
import { findCandidates } from "./reuseCandidates";
import { acquiredZenbujikoFields } from "./reuseMarks";

// 全部事項のリユースは候補と同じ目録構成でしか成立しない（BE が集合一致で検証する）。
// 依頼 1 明細につき構成は 1 つしか表現できないため、構成をまたいだチェックは
// どの候補にも一致せず対象外になる。そこでリユースのみ・取得済みのみでは
// 全部事項 4 列を個別トグルではなく「候補構成の選択」として扱う

export const sameFields = (
  a: readonly string[],
  b: readonly string[]
): boolean => a.length === b.length && a.every((field) => b.includes(field));

const OPTION_BY_FIELD: Record<string, AcquireBookZenbujiko> = {
  kyoutan: "KYODOTANPOMOKUROKU",
  sintaku: "SHINTAKUMOKUROKU",
  requestClosureBook: "HEISATOUKIBO",
};

// チェック列から依頼の目録オプションへの変換（buildBulkAcquisitionItems と同じ規則）。
// 現在事項の変換は目録全体に一括で掛かるため、_GENZAI 系と非 _GENZAI 系が混ざった
// 候補はどのチェック組み合わせからも再現できない
export const toZenbujikoOptions = (fields: readonly string[]): string[] => {
  const options = fields.flatMap((field) => OPTION_BY_FIELD[field] ?? []);
  return fields.includes("genzai")
    ? applyZenbujikoGenzaiTransform(options)
    : options;
};

/**
 * 全部事項を構成単位で扱う条件か。
 * 新規取得を使う条件は構成が一致しなくても新規で取れるため、
 * 構成を強制するとユーザーの望む組み合わせを作れなくなる
 */
export const isConstructionMode = (policy: AcquirePolicy): boolean =>
  !usesNew(policy);

/**
 * その行で選べる全部事項の構成（チェック列の集合）を候補の並び順＝取得日の新しい順で返す。
 * 構成単位で扱わない方式では空を返す。
 * 未取得のみを取得する指定では、自身の取得済みの全部事項がある行は4列とも選択不可になるため、
 * 構成の選択肢も空にする（残せばセルの不可判定と食い違う）
 */
export const eligibleZenbujikoConstructions = (
  row: BulkPropertyRow,
  policy: AcquirePolicy,
  pastDays: number,
  now: Date,
  excludeAcquired = false
): string[][] => {
  const pools = pickCandidatePool(row, policy);
  if (pools == null) {
    return [];
  }
  if (excludeAcquired && acquiredZenbujikoFields(row).size > 0) {
    return [];
  }
  return (
    pools
      .flatMap((candidates) => findCandidates(candidates, "ZENBUJIKO"))
      .filter((candidate) => isUsableByWindow(candidate, policy, pastDays, now))
      .map((candidate) => ({ candidate, fields: zenbujikoFields(candidate) }))
      // 目録なしの構成は一括取得では請求できず、対応するチェック列も存在しない。
      // チェックし直すと候補と同じ目録に戻る構成だけを選択肢として残す
      .filter(
        ({ candidate, fields }) =>
          fields.length > 0 &&
          sameFields(toZenbujikoOptions(fields), candidate.zenbujikoOptions)
      )
      .map(({ fields }) => fields)
  );
};

/**
 * 全部事項の列をクリックしたときに、その行へ適用する構成。
 * null は全部事項のチェックを全解除することを表す
 *
 * @example
 * // 候補が {共担,信託} と {共担}、現在は未チェック
 * pickConstructionForToggle(constructions, [], "kyoutan") // ["kyoutan"]
 */
export const pickConstructionForToggle = (
  constructions: string[][],
  currentFields: readonly string[],
  field: string
): string[] | null => {
  // チェック済みの列を再度押したときは構成ごと外す（部分的に外すと候補と一致しなくなる）
  if (currentFields.includes(field)) {
    return null;
  }
  const reachable = constructions.filter((fields) => fields.includes(field));
  if (reachable.length === 0) {
    return null;
  }
  // 今のチェックからの変化が最も小さい構成を選ぶ。同数なら取得日が新しい方
  const distance = (fields: string[]): number =>
    fields.filter((f) => !currentFields.includes(f)).length +
    currentFields.filter((f) => !fields.includes(f)).length;
  return reachable.reduce((best, fields) =>
    distance(fields) < distance(best) ? fields : best
  );
};

/** 現在の全部事項のチェックが、いずれかの候補構成と一致しているか（未チェックは一致扱い） */
export const matchesAnyConstruction = (
  constructions: string[][],
  currentFields: readonly string[]
): boolean =>
  currentFields.length === 0 ||
  constructions.some((fields) => sameFields(fields, currentFields));
